import { apiCall, ApiResult, IS_STATIC_DEMO } from './core';
import { clearToken } from '../../utils/auth';

export const assessmentAPI = {
  getAssessments: async (userRole: string, _tenantId: string | null, clientId: string | null = null): Promise<ApiResult> => {
    try {
      let endpoint = '/assessments';
      // For AWS users, client_id is required
      if (userRole === 'aws') {
        if (!clientId) {
          return { success: true, data: [] };
        }
        endpoint += `?client_id=${clientId}`;
      } else if (clientId) {
        endpoint += `?client_id=${clientId}`;
      }
      const data = await apiCall(endpoint);
      // Handle both array response and object with assessments property
      const assessments = Array.isArray(data) ? data : (data.assessments || []);
      return { success: true, data: assessments };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  getAssessment: async (assessmentId: string): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/assessments/${assessmentId}`);
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  submitAssessment: async (responses: Record<string, any>, clientId: string | null = null): Promise<ApiResult> => {
    try {
      const payload: Record<string, any> = {
        responses: responses
      };

      // Include client_id if provided (for partners creating assessments on behalf of clients)
      if (clientId) {
        payload.client_id = clientId;
      }

      const data = await apiCall('/assessments', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  calculateAssessment: async (responses: Record<string, any>, clientId: string | null = null): Promise<ApiResult> => {
    try {
      const payload: Record<string, any> = {
        responses: responses
      };

      // Include client_id if provided (for partners creating assessments on behalf of clients)
      if (clientId) {
        payload.client_id = clientId;
      }

      const data = await apiCall('/assessments/', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  submitDraftAssessment: async (assessmentId: string, responses: Record<string, any>): Promise<ApiResult> => {
    try {
      const payload = {
        responses: responses
      };

      const data = await apiCall(`/assessments/${assessmentId}/submit`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  deleteDraftAssessment: async (assessmentId: string): Promise<ApiResult> => {
    try {
      await apiCall(`/assessments/${assessmentId}`, {
        method: 'DELETE',
      });
      return { success: true };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  createDraftAssessment: async (clientId: string | null = null): Promise<ApiResult> => {
    try {
      const payload: Record<string, any> = {};

      // Include client_id if provided (for partners creating drafts on behalf of clients)
      if (clientId) {
        payload.client_id = clientId;
      }

      const data = await apiCall('/assessments/draft', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  getAssessmentResults: async (assessmentId: string): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/assessments/${assessmentId}/results`);
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  getLatestCompletedResponses: async (clientId?: string): Promise<ApiResult> => {
    try {
      const endpoint = clientId
        ? `/assessments/latest-completed?client_id=${clientId}`
        : '/assessments/latest-completed';
      const data = await apiCall(endpoint);
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic contact/assessment data
  submitContact: async (contactData: { email?: string; prospectId?: string; description?: string; responses?: Record<string, any>; assessmentResults?: any }): Promise<ApiResult> => {
    try {
      const data = await apiCall('/assessments/contact', {
        method: 'POST',
        body: JSON.stringify({
          ...(contactData.email && { email: contactData.email }),
          prospectId: contactData.prospectId || '',
          requestType: 'ai_specialist_review',
          subject: 'AI Strategy Assessment Review Request',
          description: contactData.description || 'I would like to schedule a consultation to review my AI strategy assessment results and discuss next steps.',
          responses: contactData.responses || {},
          assessmentResults: contactData.assessmentResults || null
        })
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  getAssessmentPDF: async (assessmentId: string): Promise<{ success: boolean; data?: Blob; error?: string }> => {
    if (IS_STATIC_DEMO) {
      return { success: false, error: 'PDF export is not available in demo mode' };
    }
    try {
      const token = localStorage.getItem('token');
      const pdfApiBase = process.env.REACT_APP_API_BASE_URL || 'https://api.lens.aivilo.ai/api/v1';

      const response = await fetch(`${pdfApiBase}/assessments/${assessmentId}/pdf`, {
        method: 'GET',
        headers: {
          'Accept': 'application/pdf',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (response.status === 401) {
        clearToken();
        localStorage.removeItem('anonymous_session_token');
        localStorage.removeItem('anonymous_session_client_id');
        localStorage.removeItem('anonymous_session_expires_at');
        if (globalThis.location.pathname !== '/login') {
          globalThis.location.href = '/login';
        }
        return { success: false, error: 'Unauthorized' };
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({})) as { message?: string };
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      // Get the PDF as a blob
      const blob = await response.blob();
      return { success: true, data: blob };
    } catch (error) {
      console.error('Error downloading PDF:', error);
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Onboarding endpoint (no authentication required)
  onboardAssessment: async (userData: Record<string, any>): Promise<any> => {
    if (IS_STATIC_DEMO) {
      return { success: true, message: 'Not available in demo mode' };
    }
    try {
      const onboardApiBase = process.env.REACT_APP_API_BASE_URL || 'https://api.lens.aivilo.ai/core/api/v1';

      const response = await fetch(`${onboardApiBase}/assessments/onboard`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({})) as { detail?: string };
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Onboarding failed:', error);
      throw error;
    }
  },

  // Join assessment endpoint (no authentication required)
  joinAssessment: async (assessmentId: string, userData: Record<string, any>): Promise<any> => {
    if (IS_STATIC_DEMO) {
      return { success: true, message: 'Not available in demo mode' };
    }
    try {
      const joinApiBase = process.env.REACT_APP_API_BASE_URL || 'https://api.lens.aivilo.ai/core/api/v1';

      const response = await fetch(`${joinApiBase}/assessments/${assessmentId}/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({})) as { detail?: string };
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Join assessment failed:', error);
      throw error;
    }
  },

  // Save draft endpoint (authenticated)
  saveDraft: async (assessmentId: string, responses: Record<string, any>): Promise<any> => {
    if (IS_STATIC_DEMO) {
      // Save to localStorage in demo mode
      localStorage.setItem(`demo_draft_${assessmentId}`, JSON.stringify(responses));
      return { success: true, responses };
    }
    try {
      return await apiCall(`/assessments/${assessmentId}/draft`, {
        method: 'PUT',
        body: JSON.stringify({ responses })
      });
    } catch (error) {
      console.error('Save draft failed:', error);
      throw error;
    }
  },

  // Load draft endpoint (authenticated)
  loadDraft: async (assessmentId: string): Promise<any> => {
    if (IS_STATIC_DEMO) {
      // Load from localStorage in demo mode
      const stored = localStorage.getItem(`demo_draft_${assessmentId}`);
      const responses = stored ? JSON.parse(stored) : {};
      return { responses, isDraft: true, status: 'draft' };
    }
    try {
      return await apiCall(`/assessments/${assessmentId}/draft`);
    } catch (error) {
      console.error('Load draft failed:', error);
      throw error;
    }
  },

  // Invite user to assessment (requires authentication)
  inviteToAssessment: async (assessmentId: string, userData: Record<string, any>): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/assessments/${assessmentId}/invite`, {
        method: 'POST',
        body: JSON.stringify(userData)
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Generate share link for assessment (requires authentication)
  generateShareLink: async (assessmentId: string): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/assessments/${assessmentId}/share-link`, {
        method: 'POST'
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  },

  // Start anonymous session (no authentication required)
  startAnonymousSession: async (assessmentId: string, shareToken: string): Promise<any> => {
    if (IS_STATIC_DEMO) {
      return { success: true, message: 'Not available in demo mode' };
    }
    try {
      const sessionApiBase = process.env.REACT_APP_API_BASE_URL || 'https://api.lens.aivilo.ai/core/api/v1';

      const response = await fetch(`${sessionApiBase}/assessments/start-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          assessment_id: assessmentId,
          share_token: shareToken,
          hours_valid: 24
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({})) as { detail?: string };
        // Check for expired token (410 Gone)
        if (response.status === 410) {
          throw new Error('This assessment link has expired. Please contact the organization for a new link.');
        }
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Start anonymous session failed:', error);
      throw error;
    }
  },

  // Regenerate share link (requires authentication)
  regenerateShareLink: async (assessmentId: string): Promise<ApiResult> => {
    try {
      const data = await apiCall(`/assessments/${assessmentId}/regenerate-link`, {
        method: 'POST'
      });
      return { success: true, data };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { success: false, error: err.message };
    }
  }
};
